import { Injectable } from '@angular/core';
import { AlertController, LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

export interface AlertOption {
  option: boolean;
}

export interface AlertProperties {
  header: string;
  subHeader: string;
  message: string;
  buttons: string[];
  cssClass: string;
}

@Injectable()
export class AlertService {
  cssClass = '';
  customConfirmationAlert: HTMLIonAlertElement;

  constructor(public alertCtrl: AlertController, private loadingController: LoadingController, private translate: TranslateService) {}

  public populateAlert(header: string, subHeader: string, message: string, button: string, cssClass?: string) {
    const alert: AlertProperties = {
      header,
      subHeader,
      message,
      buttons: [button],
      cssClass,
    };
    return alert;
  }

  public async presentAlert(header: string, subHeader: string, message: string, button: string, cssClass?: string) {
    const properties: AlertProperties = this.populateAlert(header, subHeader, message, button, cssClass);
    const alert = await this.alertCtrl.create(properties);
    await this.present(alert);
  }

  /* istanbul ignore next reason: wrong tests */
  public async presentConfirmation(header: string, subHeader: string, message: string, cssClass: string): Promise<AlertOption> {
    return new Promise(async resolve => {
      const translations = await this.translate.get(['AlertService.cancel', 'AlertService.submit']).toPromise();
      const alert = await this.alertCtrl.create({
        header,
        subHeader,
        message,
        cssClass,
        buttons: [
          {
            text: translations['AlertService.cancel'],
            role: 'cancel',
            handler: /* istanbul ignore next  */ () => {
              resolve({ option: false });
            },
          },
          {
            text: translations['AlertService.submit'],
            handler: /* istanbul ignore next  */ () => {
              resolve({ option: true });
            },
          },
        ],
      });
      await this.present(alert);
    });
  }

  /* istanbul ignore next reason: wrong tests */
  public async presentCustomConfirmation(
    header: string,
    subHeader: string,
    message: string,
    cancel: string,
    confirm: string,
    cssClass: string,
    translucent = false
  ): Promise<AlertOption> {
    return new Promise(async resolve => {
      let resolved = false;
      const alert = await this.alertCtrl.create({
        header,
        subHeader,
        message,
        cssClass,
        mode: 'ios',
        translucent,
        buttons: [
          cancel
            ? {
                text: cancel,
                role: 'cancel',
                handler: /* istanbul ignore next  */ () => {
                  resolved = true;
                  resolve({ option: false });
                },
              }
            : null,
          confirm
            ? {
                text: confirm,
                handler: /* istanbul ignore next  */ () => {
                  resolved = true;
                  resolve({ option: true });
                },
              }
            : null,
        ].filter(Boolean),
      });
      await this.present(alert);
      this.customConfirmationAlert = alert;
      alert.onDidDismiss().then(overlay => {
        if (!resolved) {
          resolve({ option: false });
        }
        this.customConfirmationAlert = null;
      });
    });
  }

  public async presentAlertConfirmation(
    header: string,
    subHeader: string,
    message: string,
    confirm?: string,
    cssClass?: string
  ): Promise<AlertOption> {
    return new Promise(async resolve => {
      const alert = await this.alertCtrl.create({
        header,
        subHeader,
        message,
        cssClass,
        buttons: [
          {
            text: confirm ? confirm : /* istanbul ignore next  */ this.translate.instant('CommonTexts.ok'),
            handler: /* istanbul ignore next  */ () => {
              resolve({ option: true });
            },
          },
        ],
      });
      await this.present(alert);
    });
  }

  /* istanbul ignore next */
  public async present(alert: HTMLIonAlertElement) {
    await alert.present();
  }

  public async createLoadingAlert() {
    return await this.loadingController.create({
      message: this.translate.instant('CommonTexts.pleaseWait'),
    });
  }
}
